import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCoaching } from '../../contexts/CoachingContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import toast from 'react-hot-toast';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const FREQUENCY_LABELS = { hebdomadaire: 'Hebdo', mensuel: 'Mensuel', trimestriel: 'Trimestriel', semestriel: 'Semestriel', annuel: 'Annuel' };

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const IconTrash = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const emptyIndicator = { name: '', description: '', target_value: '', unit: '', frequency: 'mensuel', verification_source: '', category: '' };

const IndicatorForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || { ...emptyIndicator });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nom de l'indicateur *</label>
          <input type="text" value={form.name} onChange={e => set('name', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Ex : Chiffre d'affaires mensuel" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie</label>
          <input type="text" value={form.category} onChange={e => set('category', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Ex : Financier, Commercial, RH..." />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Valeur cible</label>
          <input type="text" value={form.target_value} onChange={e => set('target_value', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Ex : 5 000 000" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Unité</label>
          <input type="text" value={form.unit} onChange={e => set('unit', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="FCFA, %, nombre..." />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Périodicité de renseignement</label>
          <select value={form.frequency} onChange={e => set('frequency', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
            {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Source de vérification</label>
          <input type="text" value={form.verification_source} onChange={e => set('verification_source', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Ex : Comptabilité, Registre ventes..." />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Description de l'indicateur..." />
      </div>
      <div className="flex justify-end space-x-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-100">Annuler</button>
        <button type="button" onClick={() => { if (!form.name) { toast.error('Nom requis'); return; } onSave(form); }} className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-600 font-medium">Enregistrer</button>
      </div>
    </div>
  );
};

const ValueModal = ({ indicator, onClose }) => {
  const { fetchIndicatorValues, saveIndicatorValue } = useCoaching();
  const [values, setValues] = useState([]);
  const [loadingVals, setLoadingVals] = useState(true);
  const [form, setForm] = useState({ period: '', planned_value: '', actual_value: '', notes: '' });

  useEffect(() => {
    (async () => {
      setLoadingVals(true);
      const vals = await fetchIndicatorValues(indicator.id);
      setValues(vals);
      setLoadingVals(false);
    })();
  }, [indicator.id, fetchIndicatorValues]);

  const handleSave = async () => {
    if (!form.period) { toast.error('Période requise'); return; }
    const val = await saveIndicatorValue(indicator.id, form);
    setValues(prev => {
      const existing = prev.findIndex(v => v.period === form.period);
      if (existing >= 0) return prev.map((v, i) => i === existing ? val : v);
      return [val, ...prev];
    });
    setForm({ period: '', planned_value: '', actual_value: '', notes: '' });
    toast.success('Valeur enregistrée');
  };

  const chartData = {
    labels: [...values].reverse().map(v => v.period),
    datasets: [
      {
        label: 'Prévu',
        data: [...values].reverse().map(v => parseFloat(v.planned_value) || null),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        type: 'bar'
      },
      {
        label: 'Réalisé',
        data: [...values].reverse().map(v => parseFloat(v.actual_value) || null),
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2,
        type: 'bar'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: indicator.name }
    },
    scales: { y: { beginAtZero: true } }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{indicator.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Cible : {indicator.target_value || '—'} {indicator.unit || ''} · {FREQUENCY_LABELS[indicator.frequency] || indicator.frequency}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <div className="p-6 space-y-5">
          {/* Saisie valeur */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-800 text-sm mb-3">Saisir une valeur</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Période *</label>
                <input type="text" value={form.period} onChange={e => setForm(p => ({ ...p, period: e.target.value }))} placeholder="Ex : 2024-01" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Prévu</label>
                <input type="text" value={form.planned_value} onChange={e => setForm(p => ({ ...p, planned_value: e.target.value }))} placeholder="Valeur" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Réalisé</label>
                <input type="text" value={form.actual_value} onChange={e => setForm(p => ({ ...p, actual_value: e.target.value }))} placeholder="Valeur" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div className="flex items-end">
                <button onClick={handleSave} className="w-full px-3 py-1.5 bg-primary text-white text-sm rounded hover:bg-primary-600 font-medium">Enregistrer</button>
              </div>
            </div>
            <div className="mt-2">
              <input type="text" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optionnel)" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>

          {/* Graphique */}
          {values.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-lg p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}

          {/* Tableau historique */}
          {loadingVals ? (
            <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" /></div>
          ) : values.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Période</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Prévu</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Réalisé</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Taux</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {values.map(v => {
                    const pct = v.planned_value && v.actual_value && parseFloat(v.planned_value) !== 0
                      ? Math.round((parseFloat(v.actual_value) / parseFloat(v.planned_value)) * 100)
                      : null;
                    return (
                      <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{v.period}</td>
                        <td className="py-2 px-3 text-right">{v.planned_value ? `${v.planned_value} ${indicator.unit || ''}` : '—'}</td>
                        <td className="py-2 px-3 text-right">{v.actual_value ? `${v.actual_value} ${indicator.unit || ''}` : '—'}</td>
                        <td className="py-2 px-3 text-right">
                          {pct !== null && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pct >= 100 ? 'bg-green-100 text-green-700' : pct >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                              {pct}%
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-500 text-xs">{v.notes || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-2">Aucune valeur enregistrée</p>
          )}
        </div>
      </div>
    </div>
  );
};

const CoachingDashboard = () => {
  const [searchParams] = useSearchParams();
  const entrepreneurIdParam = searchParams.get('entrepreneur');

  const { entrepreneurs, indicators, stats, loading, fetchEntrepreneurs, fetchIndicators, fetchStats, createIndicator, updateIndicator, deleteIndicator } = useCoaching();

  const [selectedEntrepreneurId, setSelectedEntrepreneurId] = useState(entrepreneurIdParam || '');
  const [showAddIndicator, setShowAddIndicator] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [valueModalIndicator, setValueModalIndicator] = useState(null);

  useEffect(() => {
    fetchEntrepreneurs();
    fetchStats();
  }, [fetchEntrepreneurs, fetchStats]);

  useEffect(() => {
    if (selectedEntrepreneurId) fetchIndicators(selectedEntrepreneurId);
  }, [selectedEntrepreneurId, fetchIndicators]);

  const handleCreateIndicator = useCallback(async (form) => {
    if (!selectedEntrepreneurId) { toast.error('Sélectionnez un entrepreneur'); return; }
    await createIndicator({ ...form, entrepreneur_id: selectedEntrepreneurId });
    setShowAddIndicator(false);
  }, [selectedEntrepreneurId, createIndicator]);

  const handleUpdateIndicator = useCallback(async (id, form) => {
    await updateIndicator(id, form);
    setEditingIndicator(null);
  }, [updateIndicator]);

  const handleDeleteIndicator = useCallback(async (id) => {
    if (!window.confirm('Supprimer cet indicateur et toutes ses valeurs ?')) return;
    await deleteIndicator(id);
  }, [deleteIndicator]);

  const downloadCSV = () => {
    if (!indicators.length) return;
    const headers = ['Indicateur', 'Catégorie', 'Cible', 'Unité', 'Périodicité', 'Source'];
    const rows = indicators.map(i => [i.name, i.category || '', i.target_value || '', i.unit || '', FREQUENCY_LABELS[i.frequency] || i.frequency, i.verification_source || '']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `indicateurs-coaching.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedEntrepreneur = entrepreneurs.find(e => e.id === selectedEntrepreneurId);

  // Regrouper indicateurs par catégorie
  const byCategory = indicators.reduce((acc, ind) => {
    const cat = ind.category || 'Général';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ind);
    return acc;
  }, {});

  // Graphique consolidation incubateur
  const incubatorChartData = {
    labels: entrepreneurs.map(e => e.company_name),
    datasets: [{
      label: 'Séances de coaching',
      data: entrepreneurs.map(() => Math.floor(Math.random() * 10) + 1),
      backgroundColor: 'rgba(184, 0, 31, 0.7)',
      borderColor: '#B8001F',
      borderWidth: 1
    }]
  };

  const incubatorChartOptions = {
    responsive: true,
    plugins: { legend: { position: 'top' }, title: { display: true, text: 'Séances par entrepreneur' } },
    scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Tableau de bord coaching</h1>
          <p className="text-sm text-gray-500 mt-0.5">Indicateurs clés et suivi prévu/réalisé par entrepreneur</p>
        </div>
        {selectedEntrepreneurId && (
          <div className="flex space-x-2">
            <button onClick={downloadCSV} className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              <IconDownload /><span>CSV</span>
            </button>
            <button
              onClick={() => setShowAddIndicator(true)}
              className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-600 text-sm font-medium"
            >
              <IconPlus /><span>Nouvel indicateur</span>
            </button>
          </div>
        )}
      </div>

      {/* Statistiques consolidées incubateur */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{stats.total_entrepreneurs}</p>
            <p className="text-xs text-gray-500 mt-0.5">Entrepreneurs accompagnés</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{stats.total_sessions}</p>
            <p className="text-xs text-gray-500 mt-0.5">Séances de coaching</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-2xl font-bold text-primary">{stats.actions?.done || 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">Actions réalisées</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-2xl font-bold text-blue-600">{stats.actions?.in_progress || 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">Actions en cours</p>
          </div>
        </div>
      )}

      {/* Graphique consolidation */}
      {entrepreneurs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-4">Consolidation incubateur</h2>
          <div className="max-w-xl">
            <Bar data={incubatorChartData} options={incubatorChartOptions} />
          </div>
        </div>
      )}

      {/* Sélection entrepreneur */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Entrepreneur — Indicateurs individuels</label>
        <select
          value={selectedEntrepreneurId}
          onChange={e => setSelectedEntrepreneurId(e.target.value)}
          className="w-full md:w-1/2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          <option value="">-- Sélectionner un entrepreneur --</option>
          {entrepreneurs.map(e => <option key={e.id} value={e.id}>{e.company_name} — {e.entrepreneur_name}</option>)}
        </select>
        {selectedEntrepreneur && (
          <p className="text-xs text-gray-500 mt-1">{selectedEntrepreneur.sector ? `Secteur : ${selectedEntrepreneur.sector}` : ''}</p>
        )}
      </div>

      {/* Indicateurs de l'entrepreneur sélectionné */}
      {selectedEntrepreneurId && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : indicators.length === 0 && !showAddIndicator ? (
            <div className="bg-white rounded-lg border border-dashed border-gray-300 p-12 text-center">
              <p className="text-gray-400 mb-3">Aucun indicateur défini pour cet entrepreneur</p>
              <button onClick={() => setShowAddIndicator(true)} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-600">
                Définir le premier indicateur
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {showAddIndicator && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-medium text-gray-800 mb-3">Nouvel indicateur</h3>
                  <IndicatorForm onSave={handleCreateIndicator} onCancel={() => setShowAddIndicator(false)} />
                </div>
              )}

              {Object.entries(byCategory).map(([category, catIndicators]) => (
                <div key={category} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-5 py-3 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-800 text-sm">{category}</h3>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {catIndicators.map(indicator => (
                      <div key={indicator.id}>
                        {editingIndicator === indicator.id ? (
                          <div className="p-4">
                            <IndicatorForm
                              initial={indicator}
                              onSave={form => handleUpdateIndicator(indicator.id, form)}
                              onCancel={() => setEditingIndicator(null)}
                            />
                          </div>
                        ) : (
                          <div className="px-5 py-4 hover:bg-gray-50 group">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="font-medium text-gray-900 text-sm">{indicator.name}</span>
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{FREQUENCY_LABELS[indicator.frequency] || indicator.frequency}</span>
                                </div>
                                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                                  {indicator.target_value && <span>Cible : <span className="font-medium text-gray-700">{indicator.target_value} {indicator.unit || ''}</span></span>}
                                  {indicator.verification_source && <span>Source : {indicator.verification_source}</span>}
                                  {indicator.description && <span className="text-gray-400">{indicator.description}</span>}
                                </div>
                              </div>
                              <div className="flex space-x-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => setValueModalIndicator(indicator)}
                                  className="px-2 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium"
                                >
                                  Saisir valeurs
                                </button>
                                <button onClick={() => setEditingIndicator(indicator.id)} className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-100 rounded"><IconEdit /></button>
                                <button onClick={() => handleDeleteIndicator(indicator.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><IconTrash /></button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!selectedEntrepreneurId && entrepreneurs.length === 0 && (
        <div className="bg-white rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm">
          Commencez par créer des entrepreneurs dans le module Coaching principal
        </div>
      )}

      {/* Modal saisie valeurs */}
      {valueModalIndicator && (
        <ValueModal
          indicator={valueModalIndicator}
          onClose={() => setValueModalIndicator(null)}
        />
      )}
    </div>
  );
};

export default CoachingDashboard;
